export const EMBROIDERY_TEXT_FILTER_ID = 'embroidery-thread-text'
export const EMBROIDERY_ICON_FILTER_ID = 'embroidery-thread'
export const EMBROIDERY_IMAGE_FILTER_ID = 'embroidery-thread-image'

// Expand filter region so the drop shadow isn't clipped at the element boundary
const filterProps = {
  x: '-10%',
  y: '-10%',
  width: '120%',
  height: '120%',
  colorInterpolationFilters: 'sRGB' as const,
}

const svgToDataUri = (svg: string) => {
  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

const STEEP_STITCH_HATCH = svgToDataUri(`
  <svg xmlns="http://www.w3.org/2000/svg" width="4" height="4" viewBox="0 0 4 4">
    <rect width="4" height="4" fill="#808080" />
    <path
      d="M-4 4C-2.75 3.88 -0.25 -3.88 1 -4M-3 5C-1.75 4.88 0.75 -2.88 2 -3M-2 6C-0.75 5.88 1.75 -1.88 3 -2M-1 7C0.25 6.88 2.75 -0.88 4 -1M0 8C1.25 7.88 3.75 0.12 5 0M1 9C2.25 8.88 4.75 1.12 6 1M2 10C3.25 9.88 5.75 2.12 7 2"
      stroke="#f0f0f0"
      stroke-linecap="round"
      stroke-opacity="0.8"
      stroke-width="0.6"
      fill="none"
    />
    <path
      d="M-4 4.45C-2.7 4.6 -0.1 -4.15 1.2 -4M-3 5.45C-1.7 5.6 0.9 -3.15 2.2 -3M-2 6.45C-0.7 6.6 1.9 -2.15 3.2 -2M-1 7.45C0.3 7.6 2.9 -1.15 4.2 -1M0 8.45C1.3 8.6 3.9 -0.15 5.2 0M1 9.45C2.3 9.6 4.9 0.85 6.2 1M2 10.45C3.3 10.6 5.9 1.85 7.2 2"
      stroke="#1f1f1f"
      stroke-linecap="round"
      stroke-opacity="0.75"
      stroke-width="0.4"
      fill="none"
    />
  </svg>
`)

const DIAGONAL_STITCH_HATCH = svgToDataUri(`
  <svg xmlns="http://www.w3.org/2000/svg" width="4" height="4" viewBox="0 0 4 4">
    <rect width="4" height="4" fill="#808080" />
    <path
      d="M-4 4C-2 3.88 2 -3.88 4 -4M-3 5C-1 4.88 3 -2.88 5 -3M-2 6C0 5.88 4 -1.88 6 -2M-1 7C1 6.88 5 -0.88 7 -1M0 8C2 7.88 6 0.12 8 0M1 9C3 8.88 7 1.12 9 1M2 10C4 9.88 8 2.12 10 2"
      stroke="#f0f0f0"
      stroke-linecap="round"
      stroke-opacity="0.8"
      stroke-width="0.6"
      fill="none"
    />
    <path
      d="M-4 4.45C-1.9 4.6 2.35 -4.15 4.45 -4M-3 5.45C-0.9 5.6 3.35 -3.15 5.45 -3M-2 6.45C0.1 6.6 4.35 -2.15 6.45 -2M-1 7.45C1.1 7.6 5.35 -1.15 7.45 -1M0 8.45C2.1 8.6 6.35 -0.15 8.45 0M1 9.45C3.1 9.6 7.35 0.85 9.45 1M2 10.45C4.1 10.6 8.35 1.85 10.45 2"
      stroke="#1f1f1f"
      stroke-linecap="round"
      stroke-opacity="0.75"
      stroke-width="0.4"
      fill="none"
    />
  </svg>
`)

const HATCH_LINE_COUNT = 8

const HATCH_LINES = Array.from({ length: HATCH_LINE_COUNT }, (_, index) => {
  const y = index * (4 / HATCH_LINE_COUNT)
  const wave = index % 2 === 0 ? 0.08 : -0.08

  return `
    <path
      d="M0 ${y} C1 ${y + wave}, 3 ${y - wave}, 4 ${y}"
      stroke="#f0f0f0"
      stroke-width="0.3"
      stroke-opacity="0.9"
      fill="none"
      stroke-linecap="round"
    />
    <path
      d="M0 ${y + 0.22} C1 ${y + 0.22 - wave}, 3 ${y + 0.22 + wave}, 4 ${y + 0.22}"
      stroke="#1f1f1f"
      stroke-width="0.5"
      stroke-opacity="0.75"
      fill="none"
      stroke-linecap="round"
    />`
}).join('')

const HORIZONTAL_STITCH_HATCH = svgToDataUri(`
  <svg xmlns="http://www.w3.org/2000/svg" width="4" height="4" viewBox="0 0 4 4">
    <rect width="4" height="4" fill="#808080" />
    ${HATCH_LINES}
  </svg>
`)

const EmbroideryFilter = ({
  id,
  hatchHref,
  hatchOpacity,
  hatchSize,
  dropShadow = { dx: 0, dy: 1, stdDeviation: 1, opacity: 0.4 },
}: {
  id: string
  hatchHref: string
  hatchOpacity: number
  hatchSize: number
  dropShadow?: { dx: number; dy: number; stdDeviation: number; opacity: number }
}) => (
  <filter id={id} {...filterProps}>
    {/* Slight desaturation — thread dye is less vivid than print */}
    <feColorMatrix in='SourceGraphic' type='saturate' values='0.75' result='threadBase' />
    {/* Deterministic hatch gives the preview repeated stitch strands */}
    <feImage
      href={hatchHref}
      x='0'
      y='0'
      width={hatchSize}
      height={hatchSize}
      preserveAspectRatio='none'
      result='hatchTile'
    />
    <feTile in='hatchTile' result='hatchPattern' />
    <feComposite in='hatchPattern' in2='threadBase' operator='in' result='hatchClipped' />
    <feComponentTransfer in='hatchClipped' result='hatch'>
      <feFuncA type='linear' slope={hatchOpacity} />
    </feComponentTransfer>
    <feBlend in='threadBase' in2='hatch' mode='overlay' result='hatched' />
    {/* Clip back to original alpha so edges don't bleed */}
    <feComposite in='hatched' in2='SourceGraphic' operator='in' result='clipped' />
    {/* Drop shadow to simulate raised stitching */}
    <feDropShadow
      in='clipped'
      dx={dropShadow.dx}
      dy={dropShadow.dy}
      stdDeviation={dropShadow.stdDeviation}
      floodOpacity={dropShadow.opacity}
    />
  </filter>
)

export const EmbroideryThreadFilter = () => (
  <svg aria-hidden width='0' height='0' style={{ position: 'absolute' }}>
    <defs>
      {/* Text: horizontal hatch, standard drop shadow */}
      <EmbroideryFilter
        id={EMBROIDERY_TEXT_FILTER_ID}
        hatchHref={HORIZONTAL_STITCH_HATCH}
        hatchOpacity={0.55}
        hatchSize={6}
        dropShadow={{ dx: 0, dy: 1, stdDeviation: 0.8, opacity: 0.5 }}
      />
      {/* Icon: tighter diagonal hatch, standard drop shadow */}
      <EmbroideryFilter
        id={EMBROIDERY_ICON_FILTER_ID}
        hatchHref={DIAGONAL_STITCH_HATCH}
        hatchOpacity={0.5}
        hatchSize={4}
        dropShadow={{ dx: 0, dy: 1, stdDeviation: 0.8, opacity: 0.5 }}
      />
      {/* Image/logo: denser hatch, softer shadow */}
      <EmbroideryFilter
        id={EMBROIDERY_IMAGE_FILTER_ID}
        hatchHref={STEEP_STITCH_HATCH}
        hatchOpacity={0.65}
        hatchSize={6}
        dropShadow={{ dx: 0, dy: 1, stdDeviation: 1, opacity: 0.4 }}
      />
    </defs>
  </svg>
)
